<template>
  <div
    class="mypage-ticket-today-entry-card-section"
    @click="handleToggleClicked(ticketType.ticketTypeId)"
  >
    <div
      class="mypage-ticket-today-entry-card-section__toggle"
      :class="{
        'mypage-ticket-today-entry-card-section__toggle--active':
          activeCardId === ticketType.ticketTypeId,
      }"
    >
      <p class="mypage-ticket-today-entry-card-section__toggle-name">
        <span class="mypage-ticket-today-entry-card-section__toggle-label">{{
          ticketType.ticketTypeName
        }}</span>
        <span class="mypage-ticket-today-entry-card-section__toggle-icon">
          <mypage-ticket-toggle-icon-parts />
        </span>
      </p>
    </div>

    <div
      v-if="activeCardId === ticketType.ticketTypeId"
      class="mypage-ticket-today-entry-card-section__body"
    >
      <div class="mypage-ticket-today-entry-card-section__table">
        <mypage-ticket-today-table-parts
          :ticketList="purchasedTickets"
          :ownOrganization="ownOrganization"
        />
      </div>

      <div class="mypage-ticket-today-entry-card-section__note">
        <p class="mypage-ticket-today-entry-card-section__note-message">
          {{ $tc('MypagePage.MypageTicketTodayPage.note') }}
        </p>
      </div>

      <div
        v-if="!acceptedDateTime"
        class="mypage-ticket-today-entry-card-section__footer"
        @click.stop="() => {}"
      >
        <atom-button
          :linkText="$tc('MypagePage.MypageTicketTodayPage.operatedByStaff')"
          @onClick="handleCheckTicketClicked(ticketType)"
        />
      </div>
      <div v-else class="mypage-ticket-today-entry-card-section__footer">
        <div class="mypage-ticket-today-entry-card-section__checkin">
          <p class="mypage-ticket-today-entry-card-section__checkin-time">{{ acceptedDateTime }}</p>
          <p class="mypage-ticket-today-entry-card-section__checkin-message">
            {{ $tc('MypagePage.MypageTicketTodayPage.accepted') }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="!!acceptedDateTime" class="mypage-ticket-today-entry-card-section__used">
      <img
        :src="require('@/assets/img/TicketPage/stamp_used.svg')"
        :alt="$tc('MypagePage.MypageTicketTodayPage.ticketUsed')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, Ref } from '@vue/composition-api'
import { orderBy } from 'lodash'
import MypageTicketToggleIconParts from '@/components/MypageTicketTodayPage/MypageTicketTodayPane/parts/MypageTicketToggleIconParts.vue'
import MypageTicketTodayTableParts from '@/components/MypageTicketTodayPage/MypageTicketTodayPane/parts/MypageTicketTodayTableParts.vue'
import AtomButton from '@/components/atoms/AtomButton.vue'
import DigitalTicketDataDocument from '@/store/stores/collectionModule/documents/digitalTicket/DigitalTicketDataDocument'
import DigitalTicketDataTicketTypeDocument from '@/store/stores/collectionModule/documents/digitalTicket/DigitalTicketDataTicketTypeDocument'
import OrganizationDocument from '@/store/stores/collectionModule/documents/organization/OrganizationDocument'
import StoreUtil from '@/store/StoreUtil'
import usePurchasedTicketData, {
  UserPurchasedTicketType,
} from '@/components/hook/ticket/usePurchasedTicketData'

export default defineComponent({
  name: 'MypageTicketTodayEntryCardSection',
  components: {
    AtomButton,
    MypageTicketTodayTableParts,
    MypageTicketToggleIconParts,
  },
  props: {
    activeCardId: {
      type: String,
      default: '',
    },
    /**
     * デジタルチケット イベント情報
     */
    ticketEvent: {
      type: Object as PropType<DigitalTicketDataDocument>,
      required: true,
    },
    /**
     * デジタルチケット 券種情報
     */
    ticketType: {
      type: Object as PropType<DigitalTicketDataTicketTypeDocument>,
      required: true,
    },
    /**
     * ログインユーザーが購入した商品情報
     */
    purchasedTicketInfo: {
      type: Object as PropType<UserPurchasedTicketType>,
      required: true,
    },
  },
  setup(props, context) {
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const { ownOrganization } = mypagePageStore
    const { getTargetTicketUsedDateTime } = usePurchasedTicketData()

    /**
     * チケット使用済みにした日時
     */
    const acceptedDateTime = computed(() =>
      getTargetTicketUsedDateTime(props.ticketEvent, props.ticketType, ownOrganization.value),
    )

    /**
     * ログインユーザーが購入したチケット
     */
    const purchasedTickets = computed(() =>
      orderBy(props.ticketType.tickets, 'order')?.filter((ticket) =>
        props.purchasedTicketInfo.productNumbers.availableToday.some(
          (productNumber) => productNumber === ticket.productNumber,
        ),
      ),
    )

    /**
     * カードの開閉が押下されたときの処理
     * @param targetTicketTypeId 券種ID
     */
    const handleToggleClicked = (targetTicketTypeId: string) => {
      context.emit('onClickToggle', targetTicketTypeId)
    }

    /**
     * 係員がこの画面を確認しますボタンが押下されたときの処理
     * @param targetTicketType 対象の券種
     */
    const handleCheckTicketClicked = (targetTicketType: DigitalTicketDataTicketTypeDocument) => {
      context.emit('onAdmissionTicketClicked', targetTicketType)
    }

    return {
      // MypagePageStore
      ownOrganization: ownOrganization as Ref<OrganizationDocument | undefined>,
      // MypageTicketTodayEntryCardSection
      availableTodayProductNumbers: props.purchasedTicketInfo.productNumbers
        .availableToday as Array<string>,
      acceptedDateTime,
      purchasedTickets,
      handleToggleClicked,
      handleCheckTicketClicked,
    }
  },
})
</script>

<style scoped lang="scss">
.mypage-ticket-today-entry-card-section {
  position: relative;
  padding: 24px;
  overflow: hidden;
  text-align: center;
  background-color: $white;
  border-radius: 10px;

  &__toggle {
    position: relative;
    z-index: 2;
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: center;
    width: 100%;

    &-name {
      position: relative;
      padding-right: 6px;
      margin-right: 12px;
    }

    &-label {
      display: inline-block;
      max-width: 244px;
      font-family: $fontSignup;
      font-size: 32px;
      font-weight: 700;
      color: $black;
      letter-spacing: 0.04em;
    }

    &-icon {
      position: absolute;
      top: 50%;
      right: -30px;
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      color: $gray75;
      transform: translateY(-50%) rotate(180deg);
    }

    &--active {
      .mypage-ticket-today-entry-card-section__toggle-icon {
        transform: translateY(-50%) rotate(0);
      }
    }
  }

  &__body {
    position: relative;
    z-index: 2;
    margin-top: 24px;
  }

  &__note {
    position: relative;
    z-index: 2;
    margin-top: 24px;

    &-message {
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.04em;
      line-height: 1.4;
      color: $gray75;
    }
  }

  &__footer {
    position: relative;
    z-index: 2;
    margin-top: 24px;
  }

  &__checkin {
    &-time {
      font-family: $fontHeading;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 0.04em;
    }

    &-message {
      font-size: 17px;
      font-weight: 500;
      letter-spacing: 0.04em;
    }
  }

  &__used {
    position: absolute;
    top: 28px;
    left: 50%;
    z-index: 1;
    width: 250px;
    height: 250px;
    transform: translateX(-50%);

    img {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
